import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  StethoscopeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon,
  AlertTriangleIcon,
  LightbulbIcon,
  ActivityIcon,
  ZapIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const Circumcision = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Circumcision", href: "/circumcision" }
  ];

  const stats = [
    { number: "99%", label: "Success Rate", icon: ShieldIcon },
    { number: "30min", label: "Procedure Time", icon: ClockIcon },
    { number: "7-10 Days", label: "Recovery Time", icon: ActivityIcon },
    { number: "600+", label: "Successful Cases", icon: AwardIcon }
  ];

  const causes = [
    "Religious and cultural reasons",
    "Medical necessity (phimosis, paraphimosis)",
    "Recurrent infections (balanitis, UTIs)",
    "Personal hygiene preferences",
    "Prevention of penile cancer",
    "Reduced risk of STDs",
    "Family tradition and choice",
    "Medical recommendation"
  ];

  const symptoms = [
    "Inability to retract foreskin (phimosis)",
    "Painful erections due to tight foreskin",
    "Recurrent infections under foreskin",
    "Difficulty in maintaining hygiene",
    "Pain during urination",
    "Swelling and redness of foreskin",
    "Foul-smelling discharge",
    "Difficulty in sexual intercourse"
  ];

  const procedures = [
    {
      name: "Laser Circumcision",
      description: "Advanced laser-assisted circumcision with minimal bleeding",
      duration: "20-30 minutes",
      recovery: "7-10 days",
      benefits: ["Minimal bleeding", "Precise cuts", "Faster healing", "Less post-op pain"]
    },
    {
      name: "Conventional Circumcision",
      description: "Traditional surgical removal of foreskin",
      duration: "30-45 minutes",
      recovery: "10-14 days",
      benefits: ["Proven technique", "Cost-effective", "High success rate", "Complete healing"]
    },
    {
      name: "Stapler Circumcision",
      description: "Device-assisted circumcision using surgical staples",
      duration: "15-20 minutes",
      recovery: "7-10 days",
      benefits: ["Quick procedure", "Minimal trauma", "Clean cuts", "Reduced bleeding"]
    }
  ];

  const prevention = [
    "Maintain proper genital hygiene",
    "Regular cleaning under foreskin",
    "Avoid harsh soaps and chemicals",
    "Keep genital area dry and clean",
    "Wear loose-fitting underwear",
    "Practice safe sexual habits",
    "Regular medical checkups",
    "Early treatment of infections"
  ];

  const diagnosis = [
    "Physical examination of penis and foreskin",
    "Assessment of phimosis severity",
    "Evaluation of retractability",
    "Check for signs of infection",
    "Medical history review",
    "Discussion of symptoms and concerns",
    "Assessment of sexual function",
    "Pre-operative counseling"
  ];

  const circumcisionStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": "Circumcision Surgery",
    "description": "Professional circumcision surgery using advanced laser and surgical techniques. Expert care for circumcision with modern methods and comprehensive recovery support.",
    "procedureType": "Surgical Treatment",
    "bodyLocation": "Genital Area",
    "preparation": "Pre-surgical consultation and preparation",
    "followup": "Post-operative care and healing monitoring",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Delhi",
        "addressCountry": "IN"
      }
    },
    "medicalSpecialty": "Urology",
    "areaServed": ["Delhi", "Gurgaon", "Noida", "Faridabad"]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Circumcision Surgery"
        description="Professional circumcision surgery at TheLivCure. Advanced laser circumcision and surgical techniques. Experienced urologists, modern facilities, 99% success rate. Book consultation today."
        keywords="circumcision surgery, laser circumcision, circumcision Delhi, circumcision doctor, circumcision specialist, urologist, circumcision cost, male circumcision, surgical circumcision"
        url="/circumcision"
        structuredData={circumcisionStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-50 via-white to-indigo-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-8">
            <div>
              <Badge className="bg-indigo-100 text-indigo-800 mb-4">Urology</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Advanced Circumcision Surgery
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                Expert circumcision procedures with advanced laser and surgical techniques. 
                Get safe and effective circumcision with minimal pain and faster recovery 
                using our state-of-the-art facilities.
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                Circumcision is a surgical procedure that involves the removal of the foreskin covering the head of the penis. 
                Our experienced urologists perform this procedure using the latest techniques including laser circumcision, 
                ensuring minimal discomfort, reduced bleeding, and faster healing for our patients.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentModal>
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 text-lg">
                    Book Free Consultation
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </BookAppointmentModal>
                <Button 
                  variant="outline" 
                  className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-8 py-3 text-lg"
                  onClick={() => openWhatsApp("9311720045", "Hi, I need consultation for circumcision")}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-2xl p-8 text-white mb-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <stat.icon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
                      <div className="text-indigo-100 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src="/image/Circumcission_1.jpg" 
                    alt="Circumcision surgery procedure" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Advanced laser circumcision procedure
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Circumcision Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Circumcision</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Circumcision is one of the oldest surgical procedures known to humanity, performed for various 
                reasons including religious, cultural, medical, and personal preferences. Modern circumcision 
                techniques have evolved to ensure safety, minimal pain, and optimal healing.
              </p>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">
                The procedure involves the surgical removal of the foreskin, the retractable fold of skin that 
                covers the head of the penis. When performed by experienced professionals using modern techniques, 
                circumcision is a safe procedure with low complication rates and excellent outcomes.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Medical Benefits</h3>
                    <p className="text-gray-600">Reduced risk of urinary tract infections and certain STDs</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Hygiene Benefits</h3>
                    <p className="text-gray-600">Easier cleaning and maintenance of genital hygiene</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Cultural & Religious</h3>
                    <p className="text-gray-600">Important ritual in many religious and cultural traditions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/Circumcission_2.jpg" 
                alt="Circumcision procedure and anatomy" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Professional circumcision procedure and anatomy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* When Circumcision is Needed Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">When Circumcision is Recommended</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Circumcision may be recommended for various medical and personal reasons. 
              Understanding when it's needed helps in making informed decisions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Phimosis</h3>
              <p className="text-sm text-gray-600">Inability to retract the foreskin over the glans</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Recurrent Infections</h3>
              <p className="text-sm text-gray-600">Frequent balanitis or urinary tract infections</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ActivityIcon className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Paraphimosis</h3>
              <p className="text-sm text-gray-600">Foreskin stuck behind the glans, causing swelling</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Personal Choice</h3>
              <p className="text-sm text-gray-600">Cultural, religious, or personal preference</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Advanced Surgical Techniques Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Surgical Techniques</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our urology team uses the most advanced circumcision techniques to ensure optimal outcomes, 
                minimal pain, and faster recovery for our patients of all ages.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ZapIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Laser Circumcision</h3>
                    <p className="text-gray-600">Advanced laser technology for precise cuts with minimal bleeding and pain</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ShieldIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Conventional Surgery</h3>
                    <p className="text-gray-600">Traditional surgical technique with proven safety and effectiveness</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <AwardIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Stapler Method</h3>
                    <p className="text-gray-600">Device-assisted procedure for quick, clean, and precise circumcision</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/Circumcission_3.jpg" 
                alt="Advanced surgical equipment" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                State-of-the-art surgical equipment for circumcision
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="causes">Causes</TabsTrigger>
              <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
              <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
              <TabsTrigger value="procedures">Procedures</TabsTrigger>
              <TabsTrigger value="prevention">Prevention</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <StethoscopeIcon className="w-6 h-6 mr-2 text-indigo-600" />
                      Surgery Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">
                      Circumcision is the surgical removal of the foreskin covering the head of the penis. 
                      Our advanced procedures include laser circumcision, conventional surgery, and stapler 
                      circumcision, all performed by experienced urologists in a sterile environment.
                    </p>
                    <p className="text-gray-600 mb-6">
                      This procedure is one of the oldest surgical operations known to humanity and is 
                      performed for various reasons including medical necessity, religious beliefs, and 
                      personal choice. Our experienced urologists ensure safe, effective procedures with 
                      excellent outcomes.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AwardIcon className="w-6 h-6 mr-2 text-indigo-600" />
                      Why Choose Our Surgery
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Expert Urologists</h4>
                          <p className="text-sm text-gray-600">Board-certified surgeons with extensive circumcision experience</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Advanced Techniques</h4>
                          <p className="text-sm text-gray-600">Latest laser and surgical methods for optimal results</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Safe Environment</h4>
                          <p className="text-sm text-gray-600">Sterile operating room with modern equipment</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-indigo-50 rounded-lg">
                  <ClockIcon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Quick Recovery</h3>
                  <p className="text-sm text-gray-600">Most patients return to normal activities within 7-10 days</p>
                </div>
                <div className="text-center p-6 bg-indigo-50 rounded-lg">
                  <ShieldIcon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">High Success Rate</h3>
                  <p className="text-sm text-gray-600">99% success rate with our advanced surgical techniques</p>
                </div>
                <div className="text-center p-6 bg-indigo-50 rounded-lg sm:col-span-2 lg:col-span-1">
                  <ZapIcon className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Minimally Invasive</h3>
                  <p className="text-sm text-gray-600">Laser and advanced surgical techniques for better outcomes</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="causes" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                    Reasons for Circumcision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {causes.map((cause, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{cause}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="symptoms" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                    Symptoms Indicating Need for Circumcision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {symptoms.map((symptom, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{symptom}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ActivityIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Pre-Procedure Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {diagnosis.map((test, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{test}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procedures" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {procedures.map((procedure, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{procedure.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{procedure.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Duration:</span>
                          <span className="text-sm font-medium">{procedure.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Recovery:</span>
                          <span className="text-sm font-medium">{procedure.recovery}</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {procedure.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckCircleIcon className="w-4 h-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldIcon className="w-6 h-6 mr-2 text-indigo-600" />
                    Surgical Process Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Pre-Surgery</h4>
                      <p className="text-sm text-gray-600">Consultation, preparation, and anesthesia</p>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Surgery</h4>
                      <p className="text-sm text-gray-600">Precise surgical procedure execution</p>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Recovery</h4>
                      <p className="text-sm text-gray-600">Post-operative care and monitoring</p>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-indigo-600 font-bold text-lg">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Follow-up</h4>
                      <p className="text-sm text-gray-600">Healing monitoring and care instructions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="prevention" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Prevention Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prevention.map((tip, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Circumcision Treatment Gallery</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See our advanced circumcision facilities and procedures in action. 
              Our modern equipment and expert care ensure the best outcomes for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/Circumcission_1.jpg" 
                alt="Circumcision procedure overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Overview</h3>
                <p className="text-sm text-gray-600">Advanced laser circumcision procedures</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/Circumcission_2.jpg" 
                alt="Circumcision anatomy and procedure" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Anatomy & Procedure</h3>
                <p className="text-sm text-gray-600">Professional circumcision procedure and anatomy</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/Circumcission_3.jpg" 
                alt="Modern surgical equipment" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Modern Equipment</h3>
                <p className="text-sm text-gray-600">State-of-the-art surgical equipment for circumcision</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-indigo-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready for Safe and Professional Circumcision?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Book your consultation today and get expert guidance from our experienced urologists.
          </p>
          <BookAppointmentModal>
            <Button className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Book Free Consultation
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </BookAppointmentModal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Circumcision;
